import ScreenWrapper from '@components/layout/screen-wrapper';
import { BrandLogo, Button, Field, Input, Stack, Text } from '@components/ui';
import OtpVerification from '@features/auth/otp-verification';
import { useOtpCooldown } from '@features/auth/otpCooldown';
import { phoneDigits, toUsPhoneNumber } from '@features/auth/phoneNumber';
import {
  type GetOtpFormValues,
  getOtpSchema,
} from '@features/auth/schemas/getOtpSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiErrorMessage } from '@lib/api/apiError';
import { useSendOtpMutation } from '@lib/api/auth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import type { AuthLocationState } from '@typings/auth';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AuthSplitCard } from '../auth-split-card';
import SignupRoleFields, { CAPABILITY } from './signup-role-fields';


const authPageSx = {
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  overflow: 'auto',
};

const authContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  height: 'auto',
  width: '100%',
};

const Signup = (): JSX.Element => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const url = new URL(window.location.href).searchParams;
  const itemId = url.get('itemID');

  const location = useLocation();
  const [userRole, setUserRole] = useState<'buyer' | 'r8tr' | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([
    CAPABILITY.INSPECTOR,
  ]);
  const [subAccount, setSubAccount] = useState(false);
  const sendOtp = useSendOtpMutation();
  const [showOtpVerificationModal, setShowOtpVerificationModal] =
    useState(false);

  const handleShowOtpVerificationModal = (): void => {
    setShowOtpVerificationModal(!showOtpVerificationModal);
  };

  const handleCapabilityToggle = (capability: string): void => {
    setCapabilities((prev) =>
      prev.includes(capability)
        ? prev.filter((c) => c !== capability)
        : [...prev, capability]
    );
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted, isValid },
  } = useForm<GetOtpFormValues>({
    defaultValues: {
      phoneNumber: '',
    },
    resolver: zodResolver(getOtpSchema),
    mode: 'onBlur',
  });

  const phoneNumber = watch('phoneNumber');
  const cooldown = useOtpCooldown(toUsPhoneNumber(phoneNumber));

  useEffect(() => {
    const state = location?.state as AuthLocationState | undefined;
    if (state?.userId) {
      setUserRole('buyer');
      setSubAccount(true);
    }
  }, [location?.state]);

  const onSubmit = (values: GetOtpFormValues): void => {
    if (!userRole) {
      toast.error('Please select a role');
      return;
    }

    if (capabilities.length === 0) {
      toast.error('Please select at least one capability');
      return;
    }

    if (cooldown.coolingDown) {
      setShowOtpVerificationModal(true);
      return;
    }

    const fullPhoneNumber = toUsPhoneNumber(values.phoneNumber);
    sendOtp
      .mutateAsync({
        phoneNumber: fullPhoneNumber,
        userRole,
        capabilities,
        forgetPasswordOTP: false,
      })
      .then(() => {
        cooldown.start();
        localStorage.setItem('userRole', userRole);
        if (capabilities.length > 0) {
          localStorage.setItem('capabilities', JSON.stringify(capabilities));
        }
        setShowOtpVerificationModal(true);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        toast.error(
          apiErrorMessage(error) || 'Something went wrong'
        );
      });
  };

  const showPhoneError =
    (isSubmitted || errors.phoneNumber) && errors.phoneNumber;

  return (
    <ScreenWrapper pageSx={authPageSx} containerSx={authContainerSx}>
      <Box
        sx={{
          m: 'auto',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <AuthSplitCard>
          <BrandLogo alt={t('common.logoAlt')} height={48} sx={{ mx: 'auto', mb: 1.5 }} />
          <Text
            variant="subtitle"
            align="center"
            sx={{ mb: 1, fontWeight: 600, display: { md: 'none' } }}
          >
            {t('auth.tagline')}
          </Text>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mb: 3.5, fontSize: '0.875rem', display: { md: 'none' } }}
          >
            {t('auth.description')}
          </Text>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
          >
            <Field
              sx={{ mb: 2.5 }}
              label={t('auth.mobileNumber')}
              error={
                showPhoneError
                  ? t(errors.phoneNumber?.message ?? '')
                  : undefined
              }
            >
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    type="tel"
                    id="phone-number"
                    invalid={Boolean(showPhoneError)}
                    placeholder={t('common.phonePlaceholder')}
                    inputProps={{ maxLength: 10 }}
                    value={phoneDigits(field.value)}
                    onChange={(event) => {
                      field.onChange(phoneDigits(event.target.value));
                    }}
                    onBlur={field.onBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {t('common.countryCode')}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Field>

            {url.has('itemID') ? null : (
              <SignupRoleFields
                userRole={userRole}
                subAccount={subAccount}
                capabilities={capabilities}
                onRoleChange={setUserRole}
                onCapabilityToggle={handleCapabilityToggle}
              />
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!isValid || !userRole || capabilities.length === 0}
              loading={sendOtp.isPending}
              sx={{ mt: 3.5 }}
            >
              {cooldown.coolingDown ? t('auth.enterCode') : 'Get Started'}
            </Button>

            <Stack
              align="center"
              gap="md"
              sx={{ mt: 'auto', pt: 5, textAlign: 'center' }}
            >
              <Text
                variant="body"
                tone="muted"
                sx={{
                  fontSize: '0.875rem',
                  '& strong': { color: 'text.primary' },
                }}
              >
                <Trans
                  i18nKey="auth.hasAccountPrompt"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() =>
                  navigator(itemId ? `/login?itemID=${itemId}` : '/login')
                }
              >
                {t('common.signIn')}
              </Button>
            </Stack>
          </Box>
        </AuthSplitCard>

        {showOtpVerificationModal && (
          <OtpVerification
            handleShowOtpVerificationModal={handleShowOtpVerificationModal}
            forgetPassword={false}
            phoneNumber={toUsPhoneNumber(phoneNumber)}
            userRole={userRole ?? undefined}
            capabilities={capabilities}
            itemId={itemId ?? undefined}
          />
        )}
      </Box>
    </ScreenWrapper>
  );
};
export default Signup;
